/** @jsx jsx */
import {
  ColumnConfig,
  Flex,
  List,
  ListCellsWrapper,
  ListHeader,
  ListHeaderCell,
  ListItem,
  ListItemCell,
  Paragraph,
} from "@eworkit/iris";
import { FC, useContext, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { jsx } from "theme-ui";

import { LazyUserAvatar } from "@/components/avatars";
import { UserContext } from "@/components/User";
import { IJobApplication } from "@/data/job-applications";
import { getJobRequestClientInfo } from "@/data/job-requests";
import { formatFullName } from "@/data/persons";
import { useFormats } from "@/hooks/Formats";
import { usePreviousRoutePath } from "@/infrastructure/Route";
import { JobApplicationStatusComponent } from "@/views/job-applications/status/JobApplicationStatusComponent";
import { JobRequestClientAvatar } from "@/views/JobRequests";
import { router } from "@/views/router";

interface JobApplicationsListProps {
  jobApplications: IJobApplication[];
}

export const JobApplicationsList: FC<JobApplicationsListProps> = ({ jobApplications }) => {
  const { t } = useTranslation("session");

  const { currentLocationState } = usePreviousRoutePath();

  const { formatDateTime } = useFormats();
  const { hasCompanyManagerPrivileges } = useContext(UserContext);
  const isManager = hasCompanyManagerPrivileges();

  const cols = useMemo(() => {
    const title: ColumnConfig = { sx: { width: ["100%", null, null, isManager ? "300px" : "425px"] } };
    const candidate: ColumnConfig = { sx: { flexGrow: 1 } };
    const client: ColumnConfig = { sx: { flexGrow: 1 } };
    const submitted: ColumnConfig = { sx: { width: [null, null, null, 150] } };
    const status: ColumnConfig = {
      sx: { display: "flex", width: [null, null, null, "130px"], justifyContent: [null, null, null, "flex-end"] },
    };

    return { title, candidate, client, submitted, status };
  }, [isManager]);

  return (
    <Flex flexDirection="column" gap="xs">
      <ListHeader>
        <ListCellsWrapper>
          <ListHeaderCell columnConfig={cols.title}>{t("JOB_APPLICATIONS.LIST.JOB_TITLE")}</ListHeaderCell>
          {isManager && (
            <ListHeaderCell columnConfig={cols.candidate}>{t("JOB_APPLICATIONS.LIST.CANDIDATE")}</ListHeaderCell>
          )}
          <ListHeaderCell columnConfig={cols.client}>{t("JOB_APPLICATIONS.LIST.CLIENT_CLIENT_CONTACT")}</ListHeaderCell>
          <ListHeaderCell columnConfig={cols.submitted}>{t("JOB_APPLICATIONS.LIST.SUBMITTED_ON")}</ListHeaderCell>
          <ListHeaderCell columnConfig={cols.status}>{t("JOB_APPLICATIONS.LIST.FILTERS.STATUS")}</ListHeaderCell>
        </ListCellsWrapper>
      </ListHeader>

      <List>
        {jobApplications.map(jobApplication => {
          const clientInfo = getJobRequestClientInfo(jobApplication.jobRequest);

          return (
            <ListItem key={jobApplication.id}>
              <ListCellsWrapper
                as={Link}
                to={{
                  pathname: router.get("job-applications.details", { jobApplicationId: jobApplication.id }).path,
                  state: currentLocationState,
                }}>
                <ListItemCell columnConfig={cols.title}>
                  <Flex gap="xs" alignItems={["left", "center"]} flexDirection={["column", "row"]}>
                    {isManager ? (
                      <LazyUserAvatar user={jobApplication.consultantProfile} />
                    ) : (
                      <JobRequestClientAvatar jobRequest={jobApplication.jobRequest} />
                    )}

                    <Flex flexDirection="column">
                      <Paragraph bold ellipsis>
                        {jobApplication.jobRequest.title}
                      </Paragraph>
                      <Paragraph color="n700">{jobApplication.jobRequest.systemId}</Paragraph>
                    </Flex>
                  </Flex>
                </ListItemCell>
                {isManager && (
                  <ListItemCell columnConfig={cols.candidate}>
                    {formatFullName(jobApplication.consultantProfile)}
                  </ListItemCell>
                )}
                <ListItemCell columnConfig={cols.client}>
                  <Flex flexDirection="column">
                    <Paragraph ellipsis>{clientInfo.name}</Paragraph>
                    <Paragraph color="n700" ellipsis>
                      {formatFullName(jobApplication.jobRequest.hiringManager)}
                    </Paragraph>
                  </Flex>
                </ListItemCell>
                <ListItemCell columnConfig={cols.submitted}>
                  {formatDateTime(jobApplication.submittedDate)}
                </ListItemCell>
                <ListItemCell columnConfig={cols.status}>
                  <JobApplicationStatusComponent status={jobApplication.status} />
                </ListItemCell>
              </ListCellsWrapper>
            </ListItem>
          );
        })}
      </List>
    </Flex>
  );
};
